/**
 * useSidebarThreadKeyboardShortcuts — thread jump hint labels + the global
 * keyboard listener that resolves shortcut commands (⌘1..⌘9 jump,
 * ⌘↑/↓ traverse, ⌘K palette, etc.) for the sidebar.
 *
 * Extracted from `useSidebarOrchestration` so the orchestration hook stays
 * under the 400-line cap.  Behaviour is unchanged: the same keydown/keyup/
 * blur handlers, the same "hide hints when all modifiers released" logic,
 * and the same navigation side effects.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import {
  resolveShortcutCommand,
  shortcutLabelForCommand,
  shouldShowThreadJumpHints,
  threadJumpCommandForIndex,
  threadJumpIndexFromCommand,
  threadTraversalDirectionFromCommand,
} from "../../keybindings";
import { resolveAdjacentThreadId } from "../Sidebar.logic";
import type { useServerKeybindings } from "../../rpc/serverState";
import type { SidebarThreadSummary } from "../../types";

// ---------------------------------------------------------------------------
// Constants / helpers (module-private)
// ---------------------------------------------------------------------------

const EMPTY_THREAD_JUMP_LABELS = new Map<string, string>();

type ShortcutContext = { readonly terminalFocus: boolean; readonly terminalOpen: boolean };
type KeybindingsSnapshot = ReturnType<typeof useServerKeybindings>;
type ThreadJumpCommand = NonNullable<ReturnType<typeof threadJumpCommandForIndex>>;

function threadJumpLabelMapsEqual(
  left: ReadonlyMap<string, string>,
  right: ReadonlyMap<string, string>,
): boolean {
  if (left === right) return true;
  if (left.size !== right.size) return false;
  for (const [key, value] of left) {
    if (right.get(key) !== value) return false;
  }
  return true;
}

function buildThreadJumpLabelMap(input: {
  keybindings: KeybindingsSnapshot;
  platform: string;
  terminalOpen: boolean;
  threadJumpCommandByKey: ReadonlyMap<string, ThreadJumpCommand>;
}): ReadonlyMap<string, string> {
  if (input.threadJumpCommandByKey.size === 0) return EMPTY_THREAD_JUMP_LABELS;

  const shortcutLabelOptions = {
    platform: input.platform,
    context: { terminalFocus: false, terminalOpen: input.terminalOpen },
  } as const;

  const mapping = new Map<string, string>();
  for (const [threadKey, command] of input.threadJumpCommandByKey) {
    const label = shortcutLabelForCommand(input.keybindings, command, shortcutLabelOptions);
    if (label) mapping.set(threadKey, label);
  }
  return mapping.size > 0 ? mapping : EMPTY_THREAD_JUMP_LABELS;
}

// ---------------------------------------------------------------------------
// Input / return types
// ---------------------------------------------------------------------------

export interface SidebarThreadKeyboardShortcutsInput {
  readonly keybindings: KeybindingsSnapshot;
  readonly platform: string;
  readonly orderedSidebarThreadKeys: readonly string[];
  readonly threadJumpCommandByKey: ReadonlyMap<string, ThreadJumpCommand>;
  readonly threadJumpThreadKeys: readonly string[];
  readonly sidebarThreadByKey: ReadonlyMap<string, SidebarThreadSummary>;
  readonly routeThreadKey: string | null;
  readonly navigateToThread: (threadRef: ScopedThreadRef) => void;
  readonly getCurrentSidebarShortcutContext: () => ShortcutContext;
  readonly showThreadJumpHints: boolean;
  readonly updateThreadJumpHintsVisibility: (nextVisible: boolean) => void;
  readonly newThreadShortcutLabelOptions: {
    readonly platform: string;
    readonly context: ShortcutContext;
  };
}

export interface SidebarThreadKeyboardShortcutsResult {
  readonly visibleThreadJumpLabelByKey: ReadonlyMap<string, string>;
  readonly commandPaletteShortcutLabel: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarThreadKeyboardShortcuts(
  input: SidebarThreadKeyboardShortcutsInput,
): SidebarThreadKeyboardShortcutsResult {
  const {
    keybindings,
    platform,
    orderedSidebarThreadKeys,
    threadJumpCommandByKey,
    threadJumpThreadKeys,
    sidebarThreadByKey,
    routeThreadKey,
    navigateToThread,
    getCurrentSidebarShortcutContext,
    showThreadJumpHints,
    updateThreadJumpHintsVisibility,
    newThreadShortcutLabelOptions,
  } = input;

  const [threadJumpLabelByKey, setThreadJumpLabelByKey] =
    useState<ReadonlyMap<string, string>>(EMPTY_THREAD_JUMP_LABELS);
  const threadJumpLabelsRef = useRef<ReadonlyMap<string, string>>(EMPTY_THREAD_JUMP_LABELS);
  threadJumpLabelsRef.current = threadJumpLabelByKey;
  const showThreadJumpHintsRef = useRef(showThreadJumpHints);
  showThreadJumpHintsRef.current = showThreadJumpHints;

  const visibleThreadJumpLabelByKey = showThreadJumpHints
    ? threadJumpLabelByKey
    : EMPTY_THREAD_JUMP_LABELS;

  useEffect(() => {
    const clearThreadJumpHints = () => {
      setThreadJumpLabelByKey((current) =>
        current === EMPTY_THREAD_JUMP_LABELS ? current : EMPTY_THREAD_JUMP_LABELS,
      );
      updateThreadJumpHintsVisibility(false);
    };
    const shouldIgnoreThreadJumpHintUpdate = (event: globalThis.KeyboardEvent) =>
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.shiftKey &&
      event.key !== "Meta" &&
      event.key !== "Control" &&
      event.key !== "Alt" &&
      event.key !== "Shift" &&
      !showThreadJumpHintsRef.current &&
      threadJumpLabelsRef.current === EMPTY_THREAD_JUMP_LABELS;

    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (shouldIgnoreThreadJumpHintUpdate(event)) return;
      const shortcutContext = getCurrentSidebarShortcutContext();
      const shouldShowHints = shouldShowThreadJumpHints(event, keybindings, {
        platform,
        context: shortcutContext,
      });
      if (!shouldShowHints) {
        if (
          showThreadJumpHintsRef.current ||
          threadJumpLabelsRef.current !== EMPTY_THREAD_JUMP_LABELS
        ) {
          clearThreadJumpHints();
        }
      } else {
        setThreadJumpLabelByKey((current) => {
          const nextLabelMap = buildThreadJumpLabelMap({
            keybindings,
            platform,
            terminalOpen: shortcutContext.terminalOpen,
            threadJumpCommandByKey,
          });
          return threadJumpLabelMapsEqual(current, nextLabelMap) ? current : nextLabelMap;
        });
        updateThreadJumpHintsVisibility(true);
      }

      if (event.defaultPrevented || event.repeat) return;

      const command = resolveShortcutCommand(event, keybindings, {
        platform,
        context: shortcutContext,
      });
      const traversalDirection = threadTraversalDirectionFromCommand(command);
      if (traversalDirection !== null) {
        const targetThreadKey = resolveAdjacentThreadId({
          threadIds: orderedSidebarThreadKeys,
          currentThreadId: routeThreadKey,
          direction: traversalDirection,
        });
        if (!targetThreadKey) return;
        const targetThread = sidebarThreadByKey.get(targetThreadKey);
        if (!targetThread) return;
        event.preventDefault();
        event.stopPropagation();
        navigateToThread(scopeThreadRef(targetThread.environmentId, targetThread.id));
        return;
      }

      const jumpIndex = threadJumpIndexFromCommand(command ?? "");
      if (jumpIndex === null) return;
      const targetThreadKey2 = threadJumpThreadKeys[jumpIndex];
      if (!targetThreadKey2) return;
      const targetThread2 = sidebarThreadByKey.get(targetThreadKey2);
      if (!targetThread2) return;
      event.preventDefault();
      event.stopPropagation();
      navigateToThread(scopeThreadRef(targetThread2.environmentId, targetThread2.id));
    };

    const onWindowKeyUp = (event: globalThis.KeyboardEvent) => {
      if (shouldIgnoreThreadJumpHintUpdate(event)) return;
      const shortcutContext = getCurrentSidebarShortcutContext();
      const shouldShowHints = shouldShowThreadJumpHints(event, keybindings, {
        platform,
        context: shortcutContext,
      });
      if (!shouldShowHints) {
        clearThreadJumpHints();
        return;
      }
      setThreadJumpLabelByKey((current) => {
        const nextLabelMap = buildThreadJumpLabelMap({
          keybindings,
          platform,
          terminalOpen: shortcutContext.terminalOpen,
          threadJumpCommandByKey,
        });
        return threadJumpLabelMapsEqual(current, nextLabelMap) ? current : nextLabelMap;
      });
      updateThreadJumpHintsVisibility(true);
    };

    const onWindowBlur = () => clearThreadJumpHints();

    window.addEventListener("keydown", onWindowKeyDown);
    window.addEventListener("keyup", onWindowKeyUp);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      window.removeEventListener("keydown", onWindowKeyDown);
      window.removeEventListener("keyup", onWindowKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [
    getCurrentSidebarShortcutContext,
    keybindings,
    navigateToThread,
    orderedSidebarThreadKeys,
    platform,
    routeThreadKey,
    sidebarThreadByKey,
    threadJumpCommandByKey,
    threadJumpThreadKeys,
    updateThreadJumpHintsVisibility,
  ]);

  const commandPaletteShortcutLabel = useMemo(
    () =>
      shortcutLabelForCommand(keybindings, "commandPalette.toggle", newThreadShortcutLabelOptions),
    [keybindings, newThreadShortcutLabelOptions],
  );

  return {
    visibleThreadJumpLabelByKey,
    commandPaletteShortcutLabel,
  };
}
